import { JobApplication, Contact } from "../components/ApplicationWizard";
import { getApiBaseUrl } from "../config/permanent-urls";
import { syncTokenBalance } from "../utils/tokenUpdate";
import { sendDebugLog, redactPII } from "../utils/debugLogging";
import { tokenRefreshService } from "./tokenRefreshService";
import {
  createInsufficientTokensError,
  handleInsufficientTokensError,
} from "../utils/insufficientTokensHandler";

// Use permanent API base URL (set in stone on 2025-10-14)
const API_BASE_URL = getApiBaseUrl();
export const JOB_APPLICATIONS_FETCH_LIMIT = 200;

// Debug logging helper - uses fetch on localhost, console.log in production (to avoid CORS)
const debugLog = (logData: any) => {
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  const safeLogData = redactPII(logData) as {
    location?: string;
    message?: string;
    data?: unknown;
    hypothesisId?: string;
    [key: string]: unknown;
  };
  const safeMessage =
    typeof safeLogData?.message === "string" ? safeLogData.message : "";
  const safeData = safeLogData?.data || {};
  if (isLocalhost) {
    fetch("http://127.0.0.1:7243/ingest/a4c88e35-b16a-4489-824c-ed4c6e858c3f", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(safeLogData),
    }).catch(() => {});
  } else {
    console.log(
      `[DEBUG ${safeLogData?.hypothesisId || "?"}] ${safeLogData?.location}: ${safeMessage}`,
      safeData,
    );
  }
};

export interface ApplicationNote {
  id: string;
  timestamp: string;
  activity?: string;
  contactPerson?: string;
  contactInfo?: string;
}

export interface ResumeSelection {
  type: "saved" | "custom";
  resumeId?: string | null;
}

export interface WizardPreferences {
  focusAreas: string[];
  tones: ("professional" | "technical" | "creative" | "executive")[];
  length: "concise" | "standard" | "comprehensive";
  keyAchievements?: string;
  careerObjective?: string;
  additionalNotes?: string;
}

export interface TailoredResumeGenerationOptions {
  qualityTier?: "standard" | "premium";
  mode?:
    | "master_resume"
    | "tailored_resume"
    | "generic_resume"
    | "privacy_safe_resume"
    | "recruiter_summary"
    | "short_summary"
    | "rewrite_experience_only"
    | "format_only"
    | "audit_only";
  constraints?: Record<string, unknown>;
  style?: Record<string, unknown>;
  conversationUpdates?: Array<Record<string, unknown>>;
  outputFiles?: Record<string, unknown>;
  applicantIdentity?: Record<string, unknown>;
  specialEmphasis?: string | string[];
  tailoringInstructions?: string;
  fileOutputRequest?: string;
  linkHandling?: string | Record<string, unknown>;
}

export interface TailoredResumeArtifactMetadataEntry {
  status?: string;
  content_type?: string;
  contentType?: string;
  filename?: string | null;
  sanitization?: {
    metadata_clean?: boolean | null;
    metadataClean?: boolean | null;
    hyperlinks_preserved?: boolean | null;
    hyperlinksPreserved?: boolean | null;
    limitations?: string[];
  };
}

export interface TailoredResumeArtifactReference {
  status?: string;
  s3_key?: string | null;
  s3Key?: string | null;
}

export interface TailoredResumePipelineData {
  generation_profile?: {
    quality_tier?: "standard" | "premium";
    model?: string;
    use_full_pipeline?: boolean;
  };
  artifact_metadata?: Record<string, TailoredResumeArtifactMetadataEntry>;
  artifacts?: Record<string, TailoredResumeArtifactReference>;
  [key: string]: unknown;
}

export type TailoredResumeStatus =
  | "not_started"
  | "wizard_in_progress"
  | "generated"
  | "editing"
  | "failed";

export interface TailoredResumeResponse {
  jobApplicationId: string;
  baseResumeId: string;
  resumeContent: string;
  resumeText?: string;
  wizardPreferences?: WizardPreferences;
  status: TailoredResumeStatus;
  createdAt: string;
  updatedAt: string;
  version?: number;
  pipelineData?: TailoredResumePipelineData;
  artifactMetadata?: Record<string, TailoredResumeArtifactMetadataEntry>;
  artifacts?: Record<string, TailoredResumeArtifactReference>;
  errorMessage?: string;
}

export type TailoredResumeArtifactType = "pdf" | "docx";

export interface TailoredResumeArtifactGenerationOptions {
  formats: TailoredResumeArtifactType[];
  outputFiles?: Record<string, unknown>;
  applicantIdentity?: Record<string, unknown>;
  linkHandling?: string | Record<string, unknown>;
}

export interface JobApplicationResponse {
  id: string;
  user_id: string;
  url: string;
  company: string | null;
  position: string | null;
  positionCore: string | null;
  positionSpecialization: string[];
  salary: string | null;
  location: string | null;
  workType: string | null;
  employmentType: string | null;
  contactInfo: string | null;
  recruiter: string | null;
  jobLevel: string | null;
  jobLevelNormalized: string | null;
  jobLevelConfidence: number | null;
  jobLevelSource: string | null;
  jobSummary: string | null;
  responsibilities: string[];
  requiredQualifications: string[];
  optionalQualifications: string[];
  additionalQualifications: string[];
  education: Array<{
    degree: string | null;
    field: string | null;
    requirement: "required" | "desired" | "both" | null;
  }>;
  technologies: string[];
  benefits: string[];
  benefitsStructured: {
    healthInsurance: string[];
    retirement: string[];
    timeOff: string[];
    allowances: string[];
    memberships: string[];
    bonusesEquity: string[];
    other: string[];
  };
  postingDate: string | null;
  postingAge: string | null;
  applicantsCount: number | null;
  applicantsText: string | null;
  description: string;
  resumeId?: string | null;
  selectedResumeId?: string | null;
  resumeSelectionType?: "saved" | "custom" | null;
  customResumeStatus?: TailoredResumeStatus | null;
  status: string;
  appliedDate: string;
  notes?: ApplicationNote[];
  notes_count?: number;
  contacts?: Contact[];
  guidedPrepSummary?: GuidedInterviewPrepSummary | null;
  guidedPrepSummaryAvailable?: boolean | null;
  fullGuidedPrepSummary?: GuidedInterviewPrepSummary | null;
  fullGuidedPrepSummaryAvailable?: boolean | null;
  guided_prep_summary?: GuidedInterviewPrepSummary | null;
  guided_prep_summary_available?: boolean | null;
  full_guided_prep_summary?: GuidedInterviewPrepSummary | null;
  full_guided_prep_summary_available?: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface JobApplicationCreateRequest {
  url: string;
  company?: string | null;
  position?: string | null;
  positionCore?: string | null;
  positionSpecialization?: string[];
  salary?: string | null;
  location?: string | null;
  workType?: string | null;
  employmentType?: string | null;
  contactInfo?: string | null;
  recruiter?: string | null;
  jobLevel?: string | null;
  jobLevelNormalized?: string | null;
  jobLevelConfidence?: number | null;
  jobLevelSource?: string | null;
  jobSummary?: string | null;
  responsibilities?: string[];
  requiredQualifications?: string[];
  optionalQualifications?: string[];
  additionalQualifications?: string[];
  education?: Array<{
    degree: string | null;
    field: string | null;
    requirement: "required" | "desired" | "both" | null;
  }>;
  technologies?: string[];
  benefits?: string[];
  benefitsStructured?: {
    healthInsurance: string[];
    retirement: string[];
    timeOff: string[];
    allowances: string[];
    memberships: string[];
    bonusesEquity: string[];
    other: string[];
  };
  postingDate?: string | null;
  postingAge?: string | null;
  applicantsCount?: number | null;
  applicantsText?: string | null;
  description: string;
  resumeId?: string | null;
  selectedResumeId?: string | null;
  resumeSelectionType?: "saved" | "custom" | null;
  customResumeStatus?: TailoredResumeStatus | null;
  status?: string;
  appliedDate: string;
  notes?: ApplicationNote[];
}

export interface JobApplicationUpdateRequest {
  url?: string;
  company?: string | null;
  position?: string | null;
  positionCore?: string | null;
  positionSpecialization?: string[];
  salary?: string | null;
  location?: string | null;
  workType?: string | null;
  employmentType?: string | null;
  contactInfo?: string | null;
  recruiter?: string | null;
  jobLevel?: string | null;
  jobLevelNormalized?: string | null;
  jobLevelConfidence?: number | null;
  jobLevelSource?: string | null;
  jobSummary?: string | null;
  responsibilities?: string[];
  requiredQualifications?: string[];
  optionalQualifications?: string[];
  additionalQualifications?: string[];
  education?: Array<{
    degree: string | null;
    field: string | null;
    requirement: "required" | "desired" | "both" | null;
  }>;
  technologies?: string[];
  benefits?: string[];
  benefitsStructured?: {
    healthInsurance: string[];
    retirement: string[];
    timeOff: string[];
    allowances: string[];
    memberships: string[];
    bonusesEquity: string[];
    other: string[];
  };
  postingDate?: string | null;
  postingAge?: string | null;
  applicantsCount?: number | null;
  applicantsText?: string | null;
  description?: string;
  resumeId?: string | null;
  selectedResumeId?: string | null;
  resumeSelectionType?: "saved" | "custom" | null;
  customResumeStatus?: TailoredResumeStatus | null;
  status?: string;
  appliedDate?: string;
  notes?: ApplicationNote[];
}

export interface JobApplicationsResponse {
  applications: JobApplicationResponse[];
  count: number;
  lastEvaluatedKey?: string; // Changed from last_evaluated_key to match frontend expectations
  pagination?: {
    limit?: number;
    has_more?: boolean;
  };
}

export interface ApplicationCountResponse {
  count: number;
}

export interface ResumeFitAnalysis {
  resumeId: string;
  resumeName: string;
  fitScore: number;
  skillsMatch: number;
  experienceMatch: number;
  educationMatch: number;
  skillsReasoning?: string;
  experienceReasoning?: string;
  educationReasoning?: string;
  overallReasoning?: string;
  strengths: string[];
  gaps: string[];
  recommendations: string[];
}

export interface ResumeFitAnalysisResponse {
  status?: "ok" | "processing" | "no_resumes" | "error";
  message?: string | null;
  errorMessage?: string | null;
  resumeFitAnalyses: ResumeFitAnalysis[];
  noResumesMessage: string | null;
}

export interface CompanyIntelligenceCategory {
  summary: string;
  score: number;
  explanation: string;
  sources: Array<{
    url: string;
    title?: string;
    snippet?: string;
  }>;
  warning?: string;
  key_facts?: string[];
  time_range?: string;
  risk_flags?: string[];
  sentiment?: "positive" | "neutral" | "negative" | string;
}

export interface CompanyIntelligenceResponse {
  status: "ok" | "need_clarification" | "no_data" | "processing";
  resolved_company?: {
    domain: string;
    confidence: number;
  };
  categories?: {
    layoffs: CompanyIntelligenceCategory;
    hirings: CompanyIntelligenceCategory;
    scandals: CompanyIntelligenceCategory;
    management: CompanyIntelligenceCategory;
    reviews_employ: CompanyIntelligenceCategory;
    reviews_customer: CompanyIntelligenceCategory;
    company_size_growth: CompanyIntelligenceCategory;
    diversity_esg: CompanyIntelligenceCategory;
    culture_benefits: CompanyIntelligenceCategory;
    competitors_market: CompanyIntelligenceCategory;
  };
  overall_score?: number;
  coverage?: {
    tavily_attempted?: boolean;
    tavily_used?: boolean;
    partial_failure_categories?: string[];
    empty_result_categories?: string[];
    total_results?: number;
  };
  warnings?: string[];
  all_sources?: Array<{
    url?: string;
    title?: string;
    snippet?: string;
    category?: string;
  }>;
  message?: string;
  candidates?: Array<{
    id: string;
    name: string;
    hq: string;
    confidence: number;
  }>;
  error?: string;
  timestamp?: string;
  source?: "cache" | "live";
}

export interface InterviewTypePrep {
  preparation_guide?: string;
  common_questions?: string[];
  focus_areas?: string[];
  tips?: string[];
  technical_topics?: string[];
  expected_problems?: string[];
  tools_frameworks?: string[];
  study_resources?: string[];
  star_method_guide?: string;
  typical_scenarios?: string[];
  frameworks?: string[];
  panel_structure?: string;
}

export interface GuidedInterviewPrepTopicSelection {
  topic: string;
  question_count: number;
  label?: string;
  description?: string;
}

export interface GuidedInterviewPrepTopicSection {
  topic_id?: string;
  topic: string;
  label?: string;
  question_count?: number;
  status?: "processing" | "completed" | "failed";
  use_live_research?: boolean;
  summary?: string;
  description?: string;
  questions?: string[];
  key_points?: string[];
  research_notes?: string[];
  citations?: Array<{
    title?: string;
    url?: string;
    snippet?: string;
  }>;
}

export interface GuidedInterviewPrepSummary {
  available?: boolean;
  prep_id?: string;
  title?: string;
  short_description?: string;
  status?: string;
  updated_at?: string;
  generated_at?: string;
  topic_count?: number;
  question_count?: number;
  estimated_wait_seconds?: number;
  resume_source?: string;
  resume_source_label?: string;
}

export interface CompanySpecificPrep {
  overview?: string; // 3-5 paragraphs comprehensive company information
  culture?: string;
  values?: string[];
  recent_news?: string[];
  interview_process?: string;
  what_company_values?: string; // What company values in candidates
}

export interface PersonalizedRecommendations {
  strengths_to_highlight?: string[];
  gaps_to_address?: string[];
  learning_resources?: string[];
  practice_suggestions?: string[];
  questions_to_ask_interviewer?: string[];
  common_mistakes_to_avoid?: string[];
  follow_up_strategies?: string[];
  company_specific_talking_points?: string[];
  red_flags_to_watch_for?: string[];
  confidence_building_tips?: string[];
}

export interface InterviewPrepResponse {
  status: "ok" | "error" | "processing";
  company: string;
  position: string;
  interview_types?: {
    hr?: InterviewTypePrep;
    professional_assessment?: InterviewTypePrep;
    behavioral?: InterviewTypePrep;
    case_study?: InterviewTypePrep;
    panel?: InterviewTypePrep;
  };
  company_specific?: CompanySpecificPrep;
  personalized_recommendations?: PersonalizedRecommendations;
  timestamp?: string;
  source?: "cache" | "live" | "application";
  error?: string;
  job_application_id?: string;
  message?: string;
  estimated_wait_seconds?: number;
  cache_key?: string;
  topic_sections?: GuidedInterviewPrepTopicSection[];
}

export type GuidedInterviewPrepType =
  | "recruiter_hr"
  | "behavioral"
  | "technical_functional"
  | "hiring_manager"
  | "system_design_case_study"
  | "panel_final_round"
  | "presentation_demo"
  | "executive";

export type GuidedInterviewPrepMode = "model_answers" | "answers_coaching";

export interface GuidedInterviewPrepInputSnapshot {
  prep_type: GuidedInterviewPrepType;
  output_mode: GuidedInterviewPrepMode;
  selected_topics?: GuidedInterviewPrepTopicSelection[];
  live_research?: boolean;
  use_live_research?: boolean;
  upcoming_round?: string;
  interviewer_role?: string;
  seniority_level?: string;
  interview_date?: string;
  focus_areas?: string[];
  concerns?: string[];
  additional_notes?: string;
}

export interface GuidedInterviewPrepRequest
  extends GuidedInterviewPrepInputSnapshot {
  prep_id?: string;
  job_application_id: string;
  company: string;
  position: string;
  job_description?: string;
  position_core?: string;
}

export interface GuidedInterviewPrepQualityCheck {
  evidence_boundary?: string;
  ambiguity_check?: string;
  scope_check?: string;
  remediation?: string;
  confidence?: string;
}

export interface GuidedInterviewPrepCompetency {
  competency?: string;
  why_it_matters?: string;
  signals_to_show?: string;
  confidence?: string;
}

export interface GuidedInterviewPrepRoleBreakdownItem {
  domain?: string;
  pressure_points?: string;
  confidence?: string;
}

export interface GuidedInterviewPrepAdvancedInsight {
  insight?: string;
  label?: string;
  confidence?: string;
  category?: string;
}

export interface GuidedInterviewPrepTopicPlanItem {
  topic?: string;
  why_it_matters?: string;
  confidence?: string;
}

export interface GuidedInterviewPrepEvidenceItem {
  area?: string;
  evidence?: string;
  why_it_matters?: string;
  confidence?: string;
}

export interface GuidedInterviewPrepEvidenceBundle {
  candidate_profile_summary?: string;
  resume_highlights?: string[];
  matched_evidence?: GuidedInterviewPrepEvidenceItem[];
  weak_evidence?: GuidedInterviewPrepEvidenceItem[];
  unsupported_claims_to_avoid?: string[];
  metrics_to_reuse?: string[];
  exact_technologies?: string[];
  exact_topic_candidates?: GuidedInterviewPrepTopicPlanItem[];
}

export interface GuidedInterviewPrepMaterialsSummary {
  company?: string;
  position?: string;
  position_core?: string;
  resume_source?: string;
  resume_source_label?: string;
  selected_resume_id?: string;
  resolved_resume_id?: string;
  base_resume_id?: string;
  materials_signature?: string;
  exact_technologies?: string[];
  topic_suggestions?: Array<{
    topic?: string;
    label?: string;
    reason?: string;
  }>;
}

export interface GuidedInterviewPrepCostBreakdown {
  planner_cost?: number;
  final_synthesis_cost?: number;
  topic_generation_cost?: number;
  total_cost?: number;
  topic_costs?: Array<{
    topic_id?: string;
    topic?: string;
    question_count?: number;
    per_question_cost?: number;
    base_cost?: number;
    live_research_add_on?: number;
    selective_rewrite_add_on?: number;
    selective_rewrite?: boolean;
    total_cost?: number;
  }>;
}

export interface GuidedInterviewPrepQuestion {
  question?: string;
  topic?: string;
  topic_id?: string;
  why_it_is_likely?: string;
  jd_or_background_trigger?: string;
  what_is_being_tested?: string;
  answer_30_second?: string;
  answer_90_second?: string;
  deep_follow_up?: string;
  interviewer_pushback?: string;
  best_recovery_answer?: string;
  strong_answer?: string;
  why_it_works?: string;
  weak_answer_pattern?: string;
  top_one_percent_answer_difference?: string;
  likely_follow_up_pushback?: string;
  likely_follow_ups?: string[];
  recovery_strategy?: string;
  trust_signals?: string[];
  doubt_signals?: string[];
  metrics_or_evidence_to_include?: string[];
  traps_failure_modes?: string[];
  answer_structure?: string;
  background_evidence_to_use?: string[];
}

export interface GuidedInterviewPrepResult {
  title?: string;
  short_description?: string;
  executive_summary?: string;
  topic_sections?: GuidedInterviewPrepTopicSection[];
  preparation_quality_check?: GuidedInterviewPrepQualityCheck;
  likely_interview_loop?: string[];
  risk_areas?: string[];
  competency_map?: GuidedInterviewPrepCompetency[];
  role_breakdown?: GuidedInterviewPrepRoleBreakdownItem[];
  advanced_interviewer_insights?: GuidedInterviewPrepAdvancedInsight[];
  question_bank?: GuidedInterviewPrepQuestion[];
  questions_to_ask_interviewer?: string[];
  brutal_gap_analysis?: string[];
  interview_positioning_statement?: string;
  evidence_bundle?: GuidedInterviewPrepEvidenceBundle;
  materials_summary?: GuidedInterviewPrepMaterialsSummary;
  generated_at?: string;
  estimated_wait_seconds?: number;
  message?: string;
}

export interface GuidedInterviewPrepRecord {
  prep_id: string;
  job_application_id?: string;
  company?: string;
  position?: string;
  prep_type?: GuidedInterviewPrepType;
  prep_type_label?: string;
  output_mode?: GuidedInterviewPrepMode;
  output_mode_label?: string;
  title?: string;
  short_description?: string;
  status: "processing" | "completed" | "failed";
  created_at?: string;
  updated_at?: string;
  input_snapshot?: GuidedInterviewPrepInputSnapshot;
  result?: GuidedInterviewPrepResult;
  topics?: GuidedInterviewPrepTopicSection[];
  topic_count?: number;
  completed_topic_count?: number;
  error?: string;
  message?: string;
  estimated_wait_seconds?: number;
  resume_source?: string;
  resume_source_label?: string;
  materials_summary?: GuidedInterviewPrepMaterialsSummary;
  planner_output?: GuidedInterviewPrepResult & {
    exact_topic_plan?: GuidedInterviewPrepTopicPlanItem[];
  };
  pipeline_state?: string;
  finalization_state?: string;
  cost_breakdown?: GuidedInterviewPrepCostBreakdown;
  changed_topic_count?: number;
  tokensRemaining?: number;
  tokensUsed?: number;
}

class JobApplicationService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async fetchWithAuthRetry(
    input: RequestInfo | URL,
    init: RequestInit,
  ): Promise<Response> {
    let response = await fetch(input, init);
    if (response.status !== 401) {
      return response;
    }

    const refreshed = await tokenRefreshService.forceRefresh();
    if (!refreshed) {
      return response;
    }

    return fetch(input, {
      ...init,
      headers: this.getAuthHeaders(),
    });
  }

  async getApplications(
    status?: string,
    limit: number = JOB_APPLICATIONS_FETCH_LIMIT,
    lastEvaluatedKey?: string,
  ): Promise<JobApplicationsResponse> {
    // #region agent log GET_APPLICATIONS_START
    const logData = {
      sessionId: "debug-session",
      runId: "run3",
      hypothesisId: "A",
      location: "jobApplicationService.ts:343",
      message: "getApplications called",
      data: {
        status,
        limit,
        lastEvaluatedKey: lastEvaluatedKey ? "present" : "absent",
        authHeadersPresent: !!localStorage.getItem("token"),
      },
      timestamp: Date.now(),
    };
    sendDebugLog(logData);
    // #endregion

    try {
      const queryParams = new URLSearchParams({
        ...(status && { status }),
        ...(limit && { limit: limit.toString() }),
        ...(lastEvaluatedKey && { lastEvaluatedKey }),
      });

      const response = await this.fetchWithAuthRetry(
        `${API_BASE_URL}/job-applications/?${queryParams}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        },
      );

      // #region agent log GET_APPLICATIONS_RESPONSE
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run3",
        hypothesisId: "C",
        location: "jobApplicationService.ts:419",
        message: "fetch response received",
        data: {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          corsHeaders: {
            "Access-Control-Allow-Origin":
              responseHeaders["access-control-allow-origin"],
            "Access-Control-Allow-Methods":
              responseHeaders["access-control-allow-methods"],
            "Access-Control-Allow-Headers":
              responseHeaders["access-control-allow-headers"],
            "Access-Control-Allow-Credentials":
              responseHeaders["access-control-allow-credentials"],
          },
        },
        timestamp: Date.now(),
      });
      // #endregion

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        } else if (response.status === 403) {
          throw new Error(
            "Access denied. You do not have permission to view applications.",
          );
        } else if (response.status === 500) {
          throw new Error("Server error. Please try again later.");
        } else {
          throw new Error(
            `Failed to fetch applications: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();

      // Transform backend response structure to match frontend expectations.
      // Backend returns: { success: true, data: [...], count: N }
      // List is returned regardless of user having resumes; never filter by resume presence.
      if (data && data.success && Array.isArray(data.data)) {
        return {
          ...data,
          applications: data.data,
        };
      }
      if (data && data.success && Array.isArray(data.applications)) {
        return data;
      }
      // Fallback: ensure applications is always an array so callers never see undefined
      return {
        ...data,
        applications: Array.isArray((data as any)?.applications)
          ? (data as any).applications
          : [],
      };
    } catch (fetchError) {
      // #region agent log GET_APPLICATIONS_FETCH_ERROR
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run3",
        hypothesisId: "A",
        location: "jobApplicationService.ts:395",
        message: "fetch call failed (CORS or network)",
        data: {
          errorType:
            fetchError instanceof Error
              ? fetchError.constructor.name
              : typeof fetchError,
          errorMessage:
            fetchError instanceof Error
              ? fetchError.message
              : String(fetchError),
          errorString: String(fetchError),
          isCorsError:
            fetchError instanceof TypeError &&
            (fetchError.message.includes("CORS") ||
              fetchError.message.includes("fetch")),
        },
        timestamp: Date.now(),
      });
      // #endregion
      if (
        fetchError instanceof TypeError &&
        fetchError.message.includes("fetch")
      ) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw fetchError;
    }
  }

  async getApplicationCount(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/job-applications/count`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to get application count: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      return data.count || data.data?.count || 0;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  /**
   * Find existing job application by URL
   * Returns the application if found, null otherwise
   */
  async findApplicationByUrl(
    url: string,
  ): Promise<JobApplicationResponse | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/by-url?url=${encodeURIComponent(url)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
        },
      );

      // 404 is expected when no application exists - return null silently
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        // Don't throw - if check fails, proceed without showing status
        return null;
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      if (data.found === false || data.data === null) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      // Don't throw - if check fails, proceed without showing status
      return null;
    }
  }

  async getApplication(
    id: string,
    includeNotes: boolean = false,
  ): Promise<JobApplicationResponse | null> {
    if (!id || typeof id !== "string" || id.trim() === "") {
      throw new Error("Application ID is required");
    }

    try {
      const url = `${API_BASE_URL}/job-applications/${id}${includeNotes ? "?includeNotes=true" : ""}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to get application: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async getResumeFitAnalysis(
    applicationId: string,
  ): Promise<ResumeFitAnalysisResponse | null> {
    if (
      !applicationId ||
      typeof applicationId !== "string" ||
      applicationId.trim() === ""
    ) {
      throw new Error("Application ID is required");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${applicationId}/resume-fit-analysis`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(
          errorData.error ||
            `Failed to get resume fit analysis: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async reprocessResumeFitAnalysis(
    applicationId: string,
    resumeId?: string,
  ): Promise<{ success: boolean; message?: string; error?: string } | null> {
    if (
      !applicationId ||
      typeof applicationId !== "string" ||
      applicationId.trim() === ""
    ) {
      throw new Error("Application ID is required");
    }

    const body: { resumeId?: string } = {};
    if (resumeId != null && resumeId.trim() !== "") {
      body.resumeId = resumeId;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${applicationId}/resume-fit-analysis/reprocess`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
          body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(
          errorData.error ||
            `Failed to reprocess resume fit analysis: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async getCompanyIntelligence(
    company: string,
    hints?: {
      url?: string;
      country?: string;
      state?: string;
      city?: string;
      industry?: string;
    },
    clarification?: {
      selected_candidate_id?: string | null;
      merge_locations?: boolean;
    },
    weights?: {
      scandals?: number;
      layoffs?: number;
      hirings?: number;
      mgmt?: number;
      reviews_employ?: number;
      reviews_customer?: number;
    },
    options?: {
      forceRefresh?: boolean;
      jobApplicationId?: string;
    },
  ): Promise<CompanyIntelligenceResponse> {
    if (!company || typeof company !== "string" || company.trim() === "") {
      throw new Error("Company name is required");
    }

    try {
      const requestBody: any = { company };

      if (hints) {
        requestBody.hints = hints;
      }

      if (clarification) {
        requestBody.clarification = clarification;
      }

      if (weights) {
        requestBody.weights = weights;
      }

      if (options?.forceRefresh) {
        requestBody.force_refresh = true;
      }

      if (options?.jobApplicationId) {
        requestBody.job_application_id = options.jobApplicationId;
      }

      const response = await fetch(`${API_BASE_URL}/company-intel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }

        const error = new Error(
          errorData.error ||
            `Failed to get company intelligence: ${response.status} ${response.statusText}`,
        );
        (error as any).status = response.status;
        (error as any).errorData = errorData;
        throw error;
      }

      const data = await response.json();
      const result = data.data || data;

      // Handle async processing status (prevents 504 timeout)
      if (result.status === "processing") {
        // Return the processing status - frontend can poll or wait
        return result;
      }

      // Sync token balance from backend response (backend is source of truth)
      if (
        result.tokensRemaining !== undefined &&
        result.tokensUsed !== undefined
      ) {
        const { syncTokenBalance } = await import("../utils/tokenUpdate");
        syncTokenBalance(result.tokensRemaining, result.tokensUsed);
      }

      return result;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async getInterviewPrep(
    company: string,
    jobDescription: string,
    position: string,
    interviewTypes: string[],
    userSkills?: string[],
    fitAnalysis?: {
      strengths?: string[];
      gaps?: string[];
      recommendations?: string[];
    },
    hints?: {
      url?: string;
      industry?: string;
    },
    positionCore?: string,
    jobApplicationId?: string,
  ): Promise<InterviewPrepResponse> {
    if (!company || typeof company !== "string" || company.trim() === "") {
      throw new Error("Company name is required");
    }

    if (!position || typeof position !== "string" || position.trim() === "") {
      throw new Error("Position is required");
    }

    // Validate interview types
    const validTypes = [
      "hr",
      "professional_assessment",
      "behavioral",
      "case_study",
      "panel",
    ];
    const invalidTypes = interviewTypes.filter(
      (type) => !validTypes.includes(type),
    );
    if (invalidTypes.length > 0) {
      throw new Error(
        `Invalid interview types: ${invalidTypes.join(", ")}. Valid types are: ${validTypes.join(", ")}`,
      );
    }

    try {
      const requestBody: any = {
        company,
        job_description: jobDescription,
        position,
        interview_types: interviewTypes,
      };

      if (userSkills && userSkills.length > 0) {
        requestBody.user_skills = userSkills;
      }

      if (fitAnalysis) {
        requestBody.fit_analysis = fitAnalysis;
      }

      if (hints) {
        requestBody.company_hints = hints;
      }

      if (positionCore) {
        requestBody.position_core = positionCore;
      }

      if (jobApplicationId) {
        requestBody.job_application_id = jobApplicationId;
      }

      const response = await fetch(`${API_BASE_URL}/interview-prep`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }

        const error = new Error(
          errorData.error ||
            `Failed to get interview prep: ${response.status} ${response.statusText}`,
        );
        (error as any).status = response.status;
        (error as any).errorData = errorData;
        throw error;
      }

      const data = await response.json();

      // #region agent log API_RESPONSE_RECEIVED
      debugLog({
        location: "jobApplicationService.ts:795",
        message: "API response received",
        data: {
          hasData: !!data,
          hasSuccess: !!data?.success,
          hasDataField: !!data?.data,
          dataKeys: data ? Object.keys(data) : [],
          prepDataStatus: data?.data?.status,
          prepDataKeys: data?.data ? Object.keys(data.data) : [],
        },
        timestamp: Date.now(),
        sessionId: "debug-session",
        runId: "run1",
        hypothesisId: "H1",
      });
      // #endregion

      // Handle standard response structure: { success: true, data: {...}, timestamp: ... }
      if (data && data.success && data.data) {
        const prepData = data.data;

        // #region agent log PREP_DATA_EXTRACTED
        debugLog({
          location: "jobApplicationService.ts:800",
          message: "Prep data extracted from response",
          data: {
            hasPrepData: !!prepData,
            prepDataStatus: prepData?.status,
            prepDataKeys: prepData ? Object.keys(prepData) : [],
            hasInterviewTypes: !!prepData?.interview_types,
            hasCompanySpecific: !!prepData?.company_specific,
            hasPersonalized: !!prepData?.personalized_recommendations,
          },
          timestamp: Date.now(),
          sessionId: "debug-session",
          runId: "run1",
          hypothesisId: "H1",
        });
        // #endregion

        // Ensure status field exists
        if (!prepData.status) {
          console.warn(
            'Interview prep response missing status field, defaulting to "ok"',
          );
          prepData.status = "ok";
          // #region agent log STATUS_DEFAULTED
          debugLog({
            location: "jobApplicationService.ts:805",
            message: "Status field defaulted to ok",
            data: { prepDataStatus: prepData.status },
            timestamp: Date.now(),
            sessionId: "debug-session",
            runId: "run1",
            hypothesisId: "H3",
          });
          // #endregion
        }

        // Sync token balance from backend response (backend is source of truth)
        if (
          prepData.tokensRemaining !== undefined &&
          prepData.tokensUsed !== undefined
        ) {
          syncTokenBalance(prepData.tokensRemaining, prepData.tokensUsed);
        }
        // #region agent log RETURNING_PREP_DATA
        debugLog({
          location: "jobApplicationService.ts:836",
          message: "Returning prep data from service",
          data: {
            prepDataStatus: prepData.status,
            prepDataKeys: Object.keys(prepData),
            hasInterviewTypes: !!prepData.interview_types,
            hasCompanySpecific: !!prepData.company_specific,
          },
          timestamp: Date.now(),
          sessionId: "debug-session",
          runId: "run1",
          hypothesisId: "H1",
        });
        // #endregion
        return prepData;
      }

      // Fallback: if data itself is the prep data (unwrapped)
      if (data && typeof data === "object" && !data.success) {
        // #region agent log FALLBACK_RESPONSE_HANDLING
        debugLog({
          location: "jobApplicationService.ts:840",
          message: "Handling fallback response structure",
          data: {
            dataKeys: Object.keys(data),
            hasCompany: !!data.company,
            hasPosition: !!data.position,
            dataStatus: data.status,
          },
          timestamp: Date.now(),
          sessionId: "debug-session",
          runId: "run1",
          hypothesisId: "H1",
        });
        // #endregion
        // Validate data structure before setting status
        const hasValidData =
          data.company &&
          data.position &&
          (data.status === "processing" ||
            data.interview_types ||
            data.company_specific ||
            data.personalized_recommendations);

        // Only default status to 'ok' if data is actually valid
        if (!data.status) {
          if (hasValidData) {
            console.warn(
              'Interview prep response missing status field, but data is valid - defaulting to "ok"',
            );
            data.status = "ok";
          } else {
            console.warn(
              'Interview prep response missing status field AND data is invalid - setting to "error"',
            );
            data.status = "error";
            data.error =
              "Invalid response structure from interview prep service";
          }
          // #region agent log STATUS_SET_IN_FALLBACK
          debugLog({
            location: "jobApplicationService.ts:850",
            message: "Status set in fallback handler",
            data: { dataStatus: data.status, hasValidData },
            timestamp: Date.now(),
            sessionId: "debug-session",
            runId: "run1",
            hypothesisId: "H3",
          });
          // #endregion
        }
        return data;
      }

      // Invalid response structure
      throw new Error("Invalid response structure from interview prep service");
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async createGuidedInterviewPrep(
    request: GuidedInterviewPrepRequest,
  ): Promise<GuidedInterviewPrepRecord> {
    const response = await fetch(`${API_BASE_URL}/interview-prep`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        ...request,
        use_live_research: Boolean(
          request.use_live_research ?? request.live_research,
        ),
        live_research: Boolean(
          request.use_live_research ?? request.live_research,
        ),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const tokenError = createInsufficientTokensError(
        errorData,
        response.status,
      );
      if (tokenError) {
        handleInsufficientTokensError(tokenError);
        throw tokenError;
      }
      throw new Error(
        errorData.error ||
          errorData.message ||
          `Failed to create interview prep: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    const result: GuidedInterviewPrepRecord = data.data || data;

    if (
      result.tokensRemaining !== undefined &&
      result.tokensUsed !== undefined
    ) {
      syncTokenBalance(result.tokensRemaining, result.tokensUsed);
    }

    return result;
  }

  async getStoredInterviewPreps(
    jobApplicationId?: string,
  ): Promise<GuidedInterviewPrepRecord[]> {
    const queryParams = new URLSearchParams();
    if (jobApplicationId) {
      queryParams.append("job_application_id", jobApplicationId);
    }

    const response = await fetch(
      `${API_BASE_URL}/interview-prep/stored?${queryParams.toString()}`,
      {
        method: "GET",
        headers: this.getAuthHeaders(),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          errorData.message ||
          `Failed to load stored interview preps: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    const result = data.data || data;
    return result.preps || [];
  }

  async getStoredInterviewPrep(
    prepId: string,
  ): Promise<GuidedInterviewPrepRecord | null> {
    const response = await fetch(
      `${API_BASE_URL}/interview-prep/stored/${prepId}`,
      {
        method: "GET",
        headers: this.getAuthHeaders(),
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          errorData.message ||
          `Failed to load interview prep: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return (data.data || data) as GuidedInterviewPrepRecord;
  }

  async deleteStoredInterviewPrep(prepId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/interview-prep/stored/${prepId}`,
      {
        method: "DELETE",
        headers: this.getAuthHeaders(),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          errorData.message ||
          `Failed to delete interview prep: ${response.status} ${response.statusText}`,
      );
    }
  }

  async createApplication(
    application: JobApplicationCreateRequest,
  ): Promise<JobApplicationResponse | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/job-applications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(application),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
          message?: string;
        };
        const detail =
          (typeof errorData.detail === "string" && errorData.detail) ||
          (typeof errorData.error === "string" && errorData.error) ||
          (typeof errorData.message === "string" && errorData.message) ||
          "";

        if (response.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        }
        if (response.status === 409) {
          throw new Error("Application already exists with this URL.");
        }
        if (response.status === 500) {
          throw new Error("Server error. Please try again later.");
        }
        throw new Error(
          detail ||
            `Failed to create application: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async updateApplication(
    id: string,
    application: JobApplicationUpdateRequest,
  ): Promise<JobApplicationResponse | null> {
    if (!id || typeof id !== "string" || id.trim() === "") {
      throw new Error("Application ID is required for update");
    }

    try {
      const response = await fetch(`${API_BASE_URL}/job-applications/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(application),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to update application: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async updateResumeSelection(
    jobApplicationId: string,
    selection: ResumeSelection,
  ): Promise<JobApplicationResponse | null> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error("Application ID is required for resume selection");
    }
    if (
      selection.type === "saved" &&
      (!selection.resumeId || selection.resumeId.trim() === "")
    ) {
      throw new Error("Resume ID is required when selecting a saved resume");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/resume-selection`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
          body: JSON.stringify(selection),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to update resume selection: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async generateTailoredResume(
    jobApplicationId: string,
    baseResumeId: string,
    wizardPreferences: WizardPreferences,
    options: TailoredResumeGenerationOptions = {},
  ): Promise<TailoredResumeResponse | null> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error(
        "Application ID is required to generate a tailored resume",
      );
    }
    if (
      !baseResumeId ||
      typeof baseResumeId !== "string" ||
      baseResumeId.trim() === ""
    ) {
      throw new Error(
        "Base resume ID is required to generate a tailored resume",
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/tailored-resume`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
          body: JSON.stringify({
            baseResumeId,
            wizardPreferences,
            qualityTier: options.qualityTier,
            mode: options.mode,
            constraints: options.constraints,
            style: options.style,
            conversationUpdates: options.conversationUpdates,
            outputFiles: options.outputFiles,
            applicantIdentity: options.applicantIdentity,
            specialEmphasis: options.specialEmphasis,
            tailoringInstructions: options.tailoringInstructions,
            fileOutputRequest: options.fileOutputRequest,
            linkHandling: options.linkHandling,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(
          errorData.error ||
            `Failed to generate tailored resume: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async pollTailoredResume(
    jobApplicationId: string,
  ): Promise<TailoredResumeResponse | null> {
    const maxWaitMs = 15 * 60 * 1000;
    const startTime = Date.now();
    let attempt = 0;

    const getPollDelayMs = (attemptNumber: number): number => {
      const scheduleMs = [2000, 2000, 3000, 3000, 5000, 8000, 13000, 15000];
      return scheduleMs[Math.min(attemptNumber, scheduleMs.length - 1)];
    };

    while (true) {
      const tailoredResume = await this.getTailoredResume(jobApplicationId);
      const status = tailoredResume?.status;

      if (status === "generated" || status === "editing") {
        return tailoredResume;
      }

      if (status === "failed") {
        throw new Error(
          tailoredResume?.errorMessage ||
            "Failed to generate tailored resume. Please try again.",
        );
      }

      if (Date.now() - startTime > maxWaitMs) {
        throw new Error(
          "Tailored resume generation is taking longer than expected. Please check again shortly.",
        );
      }

      attempt += 1;
      await new Promise((resolve) =>
        setTimeout(resolve, getPollDelayMs(attempt)),
      );
    }
  }

  async generateTailoredResumeArtifacts(
    jobApplicationId: string,
    options: TailoredResumeArtifactGenerationOptions,
  ): Promise<TailoredResumeResponse | null> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error(
        "Application ID is required to generate tailored resume artifacts",
      );
    }
    if (!options?.formats?.length) {
      throw new Error("At least one artifact format must be requested");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/tailored-resume/artifacts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
          body: JSON.stringify({
            formats: options.formats,
            outputFiles: options.outputFiles,
            applicantIdentity: options.applicantIdentity,
            linkHandling: options.linkHandling,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to generate tailored resume artifacts: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      return data?.data || data || null;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async getTailoredResumeArtifactDownloadUrl(
    jobApplicationId: string,
    artifactType: TailoredResumeArtifactType,
  ): Promise<{
    success?: boolean;
    downloadUrl?: string;
    download_url?: string;
    expiresIn?: number;
    data?: { downloadUrl?: string; download_url?: string; expiresIn?: number };
  }> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error(
        "Application ID is required to download a tailored resume artifact",
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/tailored-resume/artifacts/${artifactType}/download`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to download tailored resume artifact: ${response.status} ${response.statusText}`,
        );
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async getTailoredResume(
    jobApplicationId: string,
  ): Promise<TailoredResumeResponse | null> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error("Application ID is required to fetch a tailored resume");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/tailored-resume`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(
          errorData.error ||
            `Failed to get tailored resume: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      const result = data.data || data;

      // Backend returns null when no tailored resume exists (200 OK)
      return result || null;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async updateTailoredResume(
    jobApplicationId: string,
    resumeContent: string,
  ): Promise<TailoredResumeResponse | null> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error("Application ID is required to update a tailored resume");
    }
    if (!resumeContent || typeof resumeContent !== "string") {
      throw new Error("Resume content is required to update a tailored resume");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/tailored-resume`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
          body: JSON.stringify({ resumeContent }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(
          errorData,
          response.status,
        );
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(
          errorData.error ||
            `Failed to update tailored resume: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async deleteTailoredResume(
    jobApplicationId: string,
  ): Promise<{ deleted: boolean } | null> {
    if (
      !jobApplicationId ||
      typeof jobApplicationId !== "string" ||
      jobApplicationId.trim() === ""
    ) {
      throw new Error("Application ID is required to delete a tailored resume");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job-applications/${jobApplicationId}/tailored-resume`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...this.getAuthHeaders(),
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to delete tailored resume: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json().catch(() => null);
      if (data === null || data === undefined) {
        return null;
      }
      return data.data || data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  async deleteApplication(id: string): Promise<void> {
    if (!id || typeof id !== "string" || id.trim() === "") {
      throw new Error("Application ID is required for deletion");
    }

    try {
      const response = await fetch(`${API_BASE_URL}/job-applications/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to delete application: ${response.status} ${response.statusText}`,
        );
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error. Please check your internet connection and try again.",
        );
      }
      throw error;
    }
  }

  // Helper method to convert JobApplication to JobApplicationCreateRequest
  convertToCreateRequest(
    application: JobApplication,
  ): JobApplicationCreateRequest {
    return {
      url: application.url,
      company: application.company,
      position: application.position,
      positionCore: application.positionCore,
      positionSpecialization: application.positionSpecialization,
      salary: application.salary,
      location: application.location,
      workType: application.workType,
      employmentType: application.employmentType,
      contactInfo: application.contactInfo,
      recruiter: application.recruiter,
      resumeId: application.resumeId,
      jobLevel: application.jobLevel,
      jobLevelNormalized: application.jobLevelNormalized,
      jobLevelConfidence: application.jobLevelConfidence,
      jobLevelSource: application.jobLevelSource,
      jobSummary: application.jobSummary,
      responsibilities: application.responsibilities,
      requiredQualifications: application.requiredQualifications,
      optionalQualifications: application.optionalQualifications,
      additionalQualifications: application.additionalQualifications,
      education: application.education,
      technologies: application.technologies,
      benefits: application.benefits,
      benefitsStructured: application.benefitsStructured,
      postingDate: application.postingDate,
      postingAge: application.postingAge,
      applicantsCount: application.applicantsCount,
      applicantsText: application.applicantsText,
      description: application.description,
      selectedResumeId: application.selectedResumeId,
      resumeSelectionType: application.resumeSelectionType,
      customResumeStatus: application.customResumeStatus,
      status: application.status,
      appliedDate: application.appliedDate,
      notes: application.notes,
    };
  }

  // Helper method to convert JobApplicationResponse to JobApplication
  convertToJobApplication(response: JobApplicationResponse): JobApplication {
    return {
      id: response.id,
      url: response.url,
      company: response.company,
      position: response.position,
      positionCore: response.positionCore,
      positionSpecialization: response.positionSpecialization,
      salary: response.salary,
      location: response.location,
      workType: response.workType as "remote" | "hybrid" | "on-site" | null,
      employmentType: response.employmentType as
        | "Full-time"
        | "Part-time"
        | "Contract"
        | "Temporary"
        | "Internship"
        | "Volunteer"
        | "Other"
        | null,
      contactInfo: response.contactInfo,
      recruiter: response.recruiter,
      resumeId: response.resumeId ?? null,
      jobLevel: response.jobLevel,
      jobLevelNormalized: response.jobLevelNormalized,
      jobLevelConfidence: response.jobLevelConfidence,
      jobLevelSource: response.jobLevelSource,
      jobSummary: response.jobSummary,
      responsibilities: response.responsibilities,
      requiredQualifications: response.requiredQualifications,
      optionalQualifications: response.optionalQualifications,
      additionalQualifications: response.additionalQualifications,
      education: response.education,
      technologies: response.technologies,
      benefits: response.benefits,
      benefitsStructured: response.benefitsStructured,
      postingDate: response.postingDate,
      postingAge: response.postingAge,
      applicantsCount: response.applicantsCount,
      applicantsText: response.applicantsText,
      description: response.description,
      selectedResumeId: response.selectedResumeId ?? response.resumeId ?? null,
      resumeSelectionType: response.resumeSelectionType ?? null,
      customResumeStatus: response.customResumeStatus ?? null,
      status: response.status as
        | "Initialized"
        | "Pending"
        | "Applied"
        | "Interviewing"
        | "Offer"
        | "Rejected",
      appliedDate: response.appliedDate,
      notes: response.notes ?? [], // Default to empty array if not present
      notes_count: response.notes_count ?? response.notes?.length ?? 0, // Use notes_count from backend or calculate from notes array
      contacts: response.contacts ?? [], // Include contacts array
      guidedPrepSummary:
        response.guidedPrepSummary ?? response.guided_prep_summary ?? null,
      guidedPrepSummaryAvailable:
        response.guidedPrepSummaryAvailable ??
        response.guided_prep_summary_available ??
        Boolean(response.guidedPrepSummary ?? response.guided_prep_summary),
      fullGuidedPrepSummary:
        response.fullGuidedPrepSummary ??
        response.full_guided_prep_summary ??
        null,
      fullGuidedPrepSummaryAvailable:
        response.fullGuidedPrepSummaryAvailable ??
        response.full_guided_prep_summary_available ??
        Boolean(
          response.fullGuidedPrepSummary ?? response.full_guided_prep_summary,
        ),
    };
  }

  // Contact management methods
  async addContact(
    jobApplicationId: string,
    contact: Contact,
  ): Promise<Contact> {
    const token = localStorage.getItem("jwt");
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${API_BASE_URL}/job-applications/${jobApplicationId}/contacts`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contact),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to add contact");
    }

    const data = await response.json();
    return data.data;
  }

  async updateContact(
    jobApplicationId: string,
    contactId: string,
    contact: Partial<Contact>,
  ): Promise<Contact> {
    const token = localStorage.getItem("jwt");
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${API_BASE_URL}/job-applications/${jobApplicationId}/contacts/${contactId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contact),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update contact");
    }

    const data = await response.json();
    return data.data;
  }

  async deleteContact(
    jobApplicationId: string,
    contactId: string,
  ): Promise<void> {
    const token = localStorage.getItem("jwt");
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${API_BASE_URL}/job-applications/${jobApplicationId}/contacts/${contactId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete contact");
    }
  }

  async addNote(
    jobApplicationId: string,
    note: ApplicationNote,
  ): Promise<ApplicationNote> {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${API_BASE_URL}/job-applications/${jobApplicationId}/notes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(note),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to add note");
    }

    const data = await response.json();
    return data.data;
  }

  async updateNote(
    jobApplicationId: string,
    noteId: string,
    note: ApplicationNote,
  ): Promise<ApplicationNote> {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${API_BASE_URL}/job-applications/${jobApplicationId}/notes/${noteId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(note),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update note");
    }

    const data = await response.json();
    return data.data;
  }

  async deleteNote(jobApplicationId: string, noteId: string): Promise<void> {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${API_BASE_URL}/job-applications/${jobApplicationId}/notes/${noteId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete note");
    }
  }
}

export const jobApplicationService = new JobApplicationService();
